import { where } from 'firebase/firestore'
import { CONST } from '../const/const'
import { MESSAGES_CONST } from '../const/messages-const'
import { IUserInterface } from '../models/users/user.interface'
import FirestoreService from '../services/firestoreService'
import { UserPublicModel } from '../models/users-public/users-public.model'

// Constants
const COLLECTIONS = CONST.DATA.FIRESTORE.LATEST.COLLECTIONS

// Types
type IFileObjToBase64Fn = (file: File) => Promise<string | ArrayBuffer | null>

type ICustomErrorProperties = {
  message: string
  fileName?: string | null
  devMessage?: string | null
  lineNumber?: number | null
  moduleName?: string | null
}

interface ICapitalizeOptions {
  /** @info Default `true`, if `true` then capitalizes first letter of each word  */
  capitalizeAll?: boolean
  /** @info Default `false`, if `true` then capitalizes each letter of each word  */
  fullyCapitalize?: boolean
  /** @info If `valueToGenerateStringFrom` is a falsy value, then returnValueIfNotString is returned if passed  */
  returnValueIfNotString?: any
}

type ICapitalizeReturnValue<T extends ICapitalizeOptions> =
  T['returnValueIfNotString'] extends Exclude<any, undefined> ? string : T['returnValueIfNotString']

type ICapitalize = <T extends ICapitalizeOptions>(
  valueToCapitalize: any,
  options?: T
) => ICapitalizeReturnValue<T>

type ICreateString = (
  valueToGenerateStringFrom: (string | null | undefined)[] | string | null | undefined,
  separator?: string
) => string

export interface IReactAutoComplete {
  address_components: [
    { long_name: string; short_name: string; types: [string, string] },
    { long_name: string; short_name: string; types: string[] },
    { long_name: string; short_name: string; types: string[] },
    { long_name: string; short_name: string; types: string[] },
  ]
  formatted_address: string
  geometry: {
    location: {
      lat: () => number
      lng: () => number
    }
  }
  place_id: string
  html_attributions: []
}

class CustomError {
  message: ICustomErrorProperties['message']
  fileName: ICustomErrorProperties['fileName']
  lineNumber: ICustomErrorProperties['lineNumber']
  devMessage: ICustomErrorProperties['devMessage']
  moduleName: ICustomErrorProperties['moduleName']

  constructor(args: ICustomErrorProperties) {
    const { message, fileName, lineNumber, devMessage, moduleName } = args
    this.fileName = fileName ?? null
    this.lineNumber = lineNumber ?? null
    this.moduleName = moduleName ?? null
    this.message = (message as any)?.message ?? message
    this.devMessage = (devMessage as any)?.message ?? devMessage ?? message ?? null
  }

  static common(message: string) {
    return new CustomError({ message })
  }

  static somethingWentWrong(args: ICustomErrorProperties) {
    return new CustomError(args)
  }
}

/**
 * @info Converts image url to base64
 */
const fileObjToBase64: IFileObjToBase64Fn = async (file) => {
  return await new Promise((resolve) => {
    var reader = new FileReader()
    reader.onloadend = function () {
      resolve(reader.result)
    }
    reader.readAsDataURL(file)
  })
}

/**
 * @param place Details got from React auto complete library
 * @info Sets from values according to the details got from third party lib
 */
const handleLocation = (
  place: google.maps.places.PlaceResult | React.FormEvent<HTMLInputElement>
) => {
  if (typeof place === 'object') {
    let data = place as any as IReactAutoComplete
    let lat = data?.geometry?.location?.lat()
    let lng = data?.geometry?.location?.lng()
    let formatted_address = data?.formatted_address
    if (lat && lng && formatted_address) {
      return {
        lat: data?.geometry?.location?.lat(),
        lng: data?.geometry?.location?.lng(),
      }
    }
  }

  return null
}

/**
 * Retrieves the user's current geolocation coordinates (latitude and longitude).
 *
 * @returns {Promise<{ lat: number, lng: number }>} A promise that resolves with an object
 * containing the user's latitude and longitude or rejects with an error if geolocation is
 * unavailable or an error occurs.
 *
 * @throws {Error} If geolocation is not available or if an error occurs during retrieval.
 *
 * This function relies on the Geolocation API provided by modern web browsers.
 */
function getCurrentLocation(): Promise<{ lat: number; lng: number }> {
  return new Promise((resolve, reject) => {
    if (navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition(
        function (position) {
          const lat = position.coords.latitude
          const lng = position.coords.longitude
          resolve({ lat, lng })
        },
        function (error) {
          reject(error)
        }
      )
    } else {
      reject(new Error('Geolocation is not available.'))
    }
  })
}

const getEmail = async (emailOrUserName: string): Promise<string | null> => {
  const emailQuery = FirestoreService.filterItems(COLLECTIONS.USERS_PUBLIC.NAME, [
    where(COLLECTIONS.USERS_PUBLIC.FIELDS.USER_EMAIL.KEY, '==', emailOrUserName.toLowerCase()),
  ])

  const userNameQuery = FirestoreService.filterItems(COLLECTIONS.USERS_PUBLIC.NAME, [
    where(COLLECTIONS.USERS_PUBLIC.FIELDS.USER_NAME.KEY, '==', emailOrUserName.toLowerCase()),
  ])

  const [emailBasedSnaps, userBasedSnaps] = await Promise.all([emailQuery, userNameQuery])
  const combinedSnaps = [...emailBasedSnaps.docs, ...userBasedSnaps.docs]

  if (!combinedSnaps.length) return null

  return UserPublicModel.fromFirestoreDoc(combinedSnaps[0]).toObject()?.userEmail
}

const isUsernameUnique = async (userName: string): Promise<string | null> => {
  try {
    const userNameSnaps = await FirestoreService.filterItems(COLLECTIONS.USERS_PUBLIC.NAME, [
      where(
        COLLECTIONS.USERS.FIELDS.USER_NAME_NGRAMS.KEY,
        'array-contains',
        userName.toLowerCase()
      ),
    ])

    if (userNameSnaps.size) {
      return MESSAGES_CONST.USER_NAME_EXISTS
    } else {
      return null
    }
  } catch (error: any) {
    console.error(error, 'error')
    return error?.message ?? MESSAGES_CONST.SOMETHING_WENT_WRONG
  }
}

const generateName = (fullName: string) => {
  return fullName
    .split(' ')
    .map((name) => name[0])
    .join('')
    .toUpperCase()
}

/**
 * Capitalizes a string according to the provided options.
 *
 * @param args - The configuration options.
 * @param valueToCapitalize - The value to capitalize
 * @returns The capitalized string or the specified fallback value.
 *
 * In default value for `capitalizeAll` is `true`
 * If `valueToCapitalize` is not a string, the function returns `args.returnValueIfNotString`
 * if it's provided; otherwise, it returns an empty string. If `args.capitalizeAll` is `true`,
 * all letters in the string are capitalized; otherwise, only the first letter is capitalized.
 * if `args.fullyCapitalize` is `true` then all the letters of the passed string would be capitalized.
 */
const capitalize: ICapitalize = (valueToCapitalize = '', args) => {
  let splittedString: string[] = []
  let { returnValueIfNotString, capitalizeAll = true, fullyCapitalize = false } = args ?? {}

  if (typeof valueToCapitalize !== 'string') {
    if (returnValueIfNotString) return returnValueIfNotString
    else return ''
  }

  splittedString = valueToCapitalize?.split(' ')

  if (capitalizeAll)
    splittedString = splittedString.map((currChunk) => {
      return currChunk.length
        ? `${currChunk.substring(0, 1).toUpperCase()}${currChunk.substring(1)}`
        : currChunk
    })
  else if (splittedString?.[0])
    splittedString[0] = splittedString[0].length
      ? `${splittedString[0].substring(0, 1).toUpperCase()}${splittedString[0].substring(1)}`
      : splittedString[0].toUpperCase()

  return fullyCapitalize ? splittedString.join(' ')?.toUpperCase() : splittedString.join(' ')
}

const getUserFullName = (args: {
  userName?: IUserInterface['userName'] | null
  userLastName?: IUserInterface['userLastName'] | null
  userFirstName?: IUserInterface['userFirstName'] | null
  userFullName?: IUserInterface['userFullName'] | null
}): string => {
  let fullName = ''

  if (!!args.userFullName) fullName = args.userFullName.trim()

  if (fullName) return capitalize(fullName.trim())

  // if (!!args.userFirstName) fullName = args.userFirstName.trim()

  // if (!!fullName && !!args.userLastName)
  //   fullName = `${args.userFirstName} ${args.userLastName}`.trim()

  // if (!fullName) fullName = args.userName?.trim() ?? ''

  return capitalize(fullName.trim())
}

/**
 * Creates a string from an array of values or a single value, optionally separated by a specified separator.
 *
 * @param valueToGenerateStringFrom - An array of strings, or a single string, to generate the output string from.
 * @param separator - (Optional) The separator to be used between individual values when generating the string. Default is ", ".
 * @returns A string generated from the input values, separated by the specified separator.
 *
 * @example
 * // Generate a string from an array of values with default separator
 * const result1 = createString(["apple", "banana", "cherry"]);
 * // Result: "apple, banana, cherry"
 *
 * @example
 * // Generate a string from a single value with a custom separator
 * const result2 = createString("12345", "-");
 * // Result: "1-2-3-4-5"
 *
 * @example
 * // Generate a string from an array of values with custom separator
 * const result3 = createString(["Alice", null, "Bob", undefined, "Charlie"], " | ");
 * // Result: "Alice | Bob | Charlie"
 */
const createString: ICreateString = (valueToGenerateStringFrom, separator = ', ') => {
  let valueToReturn = ''

  if (Array.isArray(valueToGenerateStringFrom))
    valueToReturn = valueToGenerateStringFrom
      .filter((currChunkOfPassedValue) => {
        return !!currChunkOfPassedValue
      })
      .join(separator)

  if (typeof valueToReturn === 'number') valueToReturn = `${valueToReturn}`
  else if (typeof valueToGenerateStringFrom === 'string') valueToReturn = valueToGenerateStringFrom

  return valueToReturn.replace(/\s+/g, ' ').trim()
}

const formatDate = (dateString: any): string => {
  const date = new Date(dateString)

  // Get the month, day, and year from the date object
  const month = (date.getMonth() + 1).toString().padStart(2, '0') // Months are 0-indexed, so we add 1
  const day = date.getDate().toString().padStart(2, '0')
  const year = date.getFullYear().toString()

  // Create the formatted date string in the MM/DD/YYYY format
  return `${month}/${day}/${year}`
}

export const toFixed = (number: number, decimals = 2) => {
  return Math.round(number * 10 ** decimals) / 10 ** decimals
}

const normalizeOrder = (order: unknown) => {
  if (!Number.isFinite(Number(order)) || Number(order) === 0) return Number.MAX_SAFE_INTEGER
  return Number(order)
}

const authHelpers = {
  getEmail,
  isUsernameUnique,
}

export {
  authHelpers,
  capitalize,
  createString,
  CustomError,
  fileObjToBase64,
  formatDate,
  generateName,
  getCurrentLocation,
  getUserFullName,
  handleLocation,
  normalizeOrder,
}
